class gTagEvents {
    constructor() {
        window.dataLayer = window.dataLayer || [];
        this.delegateBody = new Delegate(document.body);
        this.homepagePopularProductsAtcPending = false;
        this.collectionsQuickAddToCartPending = false;
        this.productStickyAddToCartPending = false;
        this.productUpsellAddToCartPending = false;
        this.cartUpsellAddToCartPending = false;
        this.lastAddedCartItems = [];
        this.lastSearchIconTs = 0;
        this.menuInteractionLastFiredTs = new WeakMap();
        this.productFinderIsOpen = false;
        this.productFinderResultsFired = false;
        this.productFinderCloseReason = null;
        this.productFinderStepSelections = {};

        this.bindEvents();
        this.observeDOMChanges();
        this.handleIntersects();
        this.observeProductFinder();

        this.interceptCartApi();
    }

    bindEvents() {
        this.delegateBody.on("click", "button[name='checkout']", this.handleCheckout.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on(
            "click",
            '[data-toggle-id="quickSearch"]',
            this.handleSearchIcon.bind(this)
        );
        this.delegateBody.on("click", ".product-slider__image, .pswp__img", this.handleProductImageClick.bind(this));
        this.delegateBody.on("click", "banner-hero .Button", this.handleHomepageHeroBannerCta.bind(this));
        this.delegateBody.on("click", '.featured-collection .Button[data-action="toggle-collection"]', this.handleHomepagePopularProductsTabs.bind(this));
        this.delegateBody.on(
            "click",
            '.header-hamburger-btn, [data-action="toggle-drawer"][data-toggle-id="mobile-menu-drawer"]',
            this.handleMenuInteraction.bind(this)
        );
        this.delegateBody.on("click", ".product-recommendation--nav-button", this.handleProductUpsellTabs.bind(this));
        this.delegateBody.on("click", '[data-action="add-to-cart"]', this.handleHomepagePopularProductsAddToCart.bind(this));
        this.delegateBody.on("click", 'main-collection-grid [data-action="add-to-cart"]', this.handleCollectionsQuickAddToCart.bind(this));
        this.delegateBody.on("click", '.pm__sticky-atc [data-action="add-to-cart"]', this.handleProductStickyAddToCart.bind(this));
        this.delegateBody.on("click", 'product-recommendations [data-action="add-to-cart"]', this.handleProductUpsellAddToCart.bind(this));
        this.delegateBody.on("click", '.cart-drawer__recommendations-wrapper [data-action="add-to-cart"]', this.handleCartUpsellAddToCart.bind(this));
        this.delegateBody.on("click", ".cart-drawer__rec-item .pc__wrapper-link", this.handleCartUpsellClick.bind(this));
        this.delegateBody.on("click", "main-collection-grid .featured_collection__toggle-button", this.handleCollectionsTabs.bind(this));
        this.delegateBody.on("click", "drawer-product-info [data-slide-id]", this.handleProductTabs.bind(this));
        this.delegateBody.on("click", '.pm__faq-button[data-slide], [data-action="toggle-drawer"][data-toggle-id="product-info-drawer"][data-slide]', this.handleProductTabs.bind(this));
        this.delegateBody.on("click", "product-selector-popup .ps-step__next-button", this.handleProductFinderStep.bind(this));
        this.delegateBody.on("click", 'product-selector-popup [data-action="refresh-selector"]', this.handleProductFinderRenew.bind(this));
        this.delegateBody.on('variant:changed:gtm', this.handleVariantChange.bind(this));
        document.body.addEventListener("submit", this.handleHomepagePopularProductsFormSubmit.bind(this), true);
        document.body.addEventListener("submit", this.handleCollectionsQuickAddToCartFormSubmit.bind(this), true);
        document.body.addEventListener("submit", this.handleProductStickyAddToCartFormSubmit.bind(this), true);
        document.body.addEventListener("submit", this.handleProductUpsellAddToCartFormSubmit.bind(this), true);
        document.body.addEventListener("submit", this.handleCartUpsellAddToCartFormSubmit.bind(this), true);
        document.body.addEventListener("product:added", this.handleProductAdded.bind(this));

        // Fallback in case Delegate isn't catching a click target (e.g. SVG child nodes).
        document.addEventListener("click", (event) => {
            const target = event.target?.closest?.('[data-action="open-drawer"][data-toggle-id="quickSearch"]');
            if (target) this.handleSearchIcon(event);
        }, true);

        // Fallback for hamburger/menu button.
        document.addEventListener("click", (event) => {
            const target = event.target?.closest?.('.header-hamburger-btn, [data-action="toggle-drawer"][data-toggle-id="mobile-menu-drawer"]');
            if (target) this.handleMenuInteraction(event);
        }, true);

        // Fallback for PDP FAQ tabs (some scripts can stop bubbling).
        document.addEventListener("click", (event) => {
            const target = event.target?.closest?.('.pm__faq-button[data-slide], [data-action="toggle-drawer"][data-toggle-id="product-info-drawer"][data-slide]');
            if (target) this.handleProductTabs(event);
        }, true);

        // The popup toggle handler in global.js calls stopImmediatePropagation() from its own
        // body-level delegate, so a bubbling listener here never runs. Capture phase instead.
        document.addEventListener("click", this.handleProductFinderClick.bind(this), true);

        // Escape is handled in global.js by re-clicking the opener button, which would otherwise
        // look identical to a CTA click. Record the real reason before that synthetic click lands.
        document.addEventListener("keydown", this.handleProductFinderEscape.bind(this), true);

        // Product finder result cards stop propagation on their overlay, so listen in capture phase.
        document.addEventListener("click", this.handleProductFinderReadMore.bind(this), true);

        document.addEventListener("DOMContentLoaded", this.setTrackingConsentCookie.bind(this));
    }

    observeDOMChanges() {
        // Defer MutationObserver until after page load to avoid blocking LCP/TBT
        const start = () => {
            let pending = false;
            const observer = new MutationObserver(() => {
                if (!pending) {
                    pending = true;
                    requestAnimationFrame(() => {
                        this.handleIntersects();
                        this.observeProductFinder();
                        pending = false;
                    });
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: false
            });
        };

        if (document.readyState === 'complete') {
            start();
        } else {
            window.addEventListener('load', start, { once: true });
        }
    }

    handleIntersects() {
        const allIntersections = document.body.querySelectorAll('[data-gtag-intersection][data-observed="false"]');

        allIntersections.forEach((item) => {
            const observer = new IntersectionObserver(
                this.handleIntersection.bind(this),
                {
                    root: null, // Use the viewport as the root
                    rootMargin: "0px",
                    threshold: 0.1, // Adjust the threshold as per your requirement
                }
            );

            observer.observe(item);
            item.dataset.observed = "true";
        });
    }

    handleIntersection(entries) {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                let data = false;

                try {
                    data = JSON.parse(
                        entry.target.querySelector('script[type="application/ld+json"]').textContent
                    );

                } catch (e) {
                }

                if (!window.dataLayer || !data) return;

                window.dataLayer.push({ ecommerce: null });
                window.dataLayer.push(data);

            }
        });
    }

    // check if okay
    // how we fetch current variant can vary depending on the theme
    handleVariantChange(event) {        
        // get current variant id from url
        const params = new URLSearchParams(window.location.search);
        const variantIdParam = params.get("variant");
    
        if (!variantIdParam) return;
        const currentVariantID = Number(variantIdParam);
    
        // find current variant json in ShopifyAnalytics
        const variants = ShopifyAnalytics.meta.product.variants;
        let currentVariant = null;
    
        variants.forEach(variant => {        
            if (variant.id === currentVariantID) currentVariant = variant;
        })
    
        // get and update gtm script
        const dataElem = document.querySelector('.main-product__gtm-script');    
        if (!currentVariant || !dataElem) return;
        
        const data = JSON.parse(dataElem.textContent);
        const item = data.ecommerce.items[0];
        
        item.item_variant = currentVariant.public_title || "Default Title";
        item.item_id = currentVariant.id || "";
        item.item_sku = currentVariant.sku || "";
        item.price = currentVariant.price / 100;
        item.quantity = 1;
    
        data.ecommerce.value = currentVariant.price / 100;
    
        if (!window.dataLayer || !data) return;
    
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(data);
    }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        window.fetch = async function (...args) {
            try {
                const [url, request] = [...args];

                // Trigger custom logic for cart clear API
                if (url.includes(`/cart/clear`)) _this.cartClear();
                if (url.includes(`/cart/update`)) _this.cartUpdate(request.body);

                const response = await originalFetch.apply(this, args);
                if (!response.ok) return response;
                
                 // Early return for non-cart-related APIs or missing conditions
                 if (url === '' || !window.dataLayer) return response;             

                // Process cart actions based on URL
                if (url.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json(); 

                    _this.cartAdd(json, request);
                } else if (url.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json(); 

                    _this.cartChange(json, url, request);
                }

                return response;

            } catch (e) {
                console.error('Fetch interceptor error:', e);
                return originalFetch(...args);
            }
        };
    }

    // check if quantity is okay
    // when adding product that already exist in a cart fetch response thats returned does not have right product quantity
    // instead of showing how many products has been added it shows how many there are in cart
    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.lastAddedCartItems = cartItems;
        this.productAdded(cartItems);

        if (this.homepagePopularProductsAtcPending) {
            this.homepagePopularProductsAtcPending = false;
            this.homepagePopularProductsAddedToCart(cartItems);
        }

        if (this.collectionsQuickAddToCartPending) {
            this.collectionsQuickAddToCartPending = false;
            this.collectionsQuickAddToCart(cartItems);
        }

        if (this.productStickyAddToCartPending) {
            this.productStickyAddToCartPending = false;
            this.productStickyAddToCart(cartItems);
        }

        if (this.productUpsellAddToCartPending) {
            this.productUpsellAddToCartPending = false;
            this.productUpsellAddToCart(cartItems);
        }

        if (this.cartUpsellAddToCartPending) {
            this.cartUpsellAddToCartPending = false;
            this.cartUpsellAddToCart(cartItems);
        }
    }

    async cartUpdate(body) {
        if (!body) return;
        const updates = JSON.parse(body).updates;
    
        const oldCart = await this.getCart();
        const cartItems = oldCart.items, itemsAdded = [], itemsRemoved = [];
    
        cartItems.forEach(item => {            
            const newQuantity = updates[item.key];
            if (typeof newQuantity === "number") {
                // Only treat as "removed from cart" when item is fully removed (quantity -> 0).
                if (newQuantity === 0) {
                    itemsRemoved.push({ ...item, quantity: item.quantity });
                    return;
                }

                // Track adds (increase only). Decreases are not "remove from cart" events.
                if (newQuantity > item.quantity) {
                    itemsAdded.push({ ...item, quantity: newQuantity - item.quantity });
                }
            }
        })
    
        this.productAdded(itemsAdded);
        this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart, url, request) {
        // Always allow "add to cart" from cart/change response.
        this.productAdded(cart.items_added);

        // Only fire remove-from-cart when the change request explicitly set quantity to 0
        // (remove button or quantity control from 1 -> 0).
        let nextQty = null;
        let lineId = null;

        try {
            const parsedUrl = new URL(url, window.location.origin);
            if (parsedUrl.searchParams.has("quantity")) {
                nextQty = Number(parsedUrl.searchParams.get("quantity"));
            }
            lineId = parsedUrl.searchParams.get("id") || parsedUrl.searchParams.get("line") || null;
        } catch (e) {}

        if (nextQty === null && request?.body) {
            try {
                const body = typeof request.body === "string" ? JSON.parse(request.body) : request.body;
                if (typeof body?.quantity === "number") nextQty = body.quantity;
                lineId = body?.id || body?.line || body?.key || lineId;
            } catch (e) {}
        }

        if (nextQty !== 0) return;

        // Build removed item payload from the cart state *before* removal.
        const oldCart = await this.getCart();
        const removedItem = oldCart?.items?.find((i) => i.key === lineId) || null;
        if (removedItem) {
            this.productsRemoved([{ ...removedItem, quantity: removedItem.quantity }]);
        } else if (cart.items_removed?.length) {
            // Fallback: if Shopify provides items_removed (full removals), use it.
            this.productsRemoved(cart.items_removed);
        }
    }

    async cartClear() {
        const cart = await this.getCart();
        this.productsRemoved(cart.items);
    }

    async getCart() {
        try {
            const response = await fetch(window.routes.localeRootUrl + "/cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            return await response.json();

        } catch (error) {
        }
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'custom_add_to_cart',
            'ecommerce': this.buildEcommerce(cartItems)
        }

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    async productsRemoved(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
              'event': "remove_from_cart",
              'ecommerce': this.buildEcommerce(cartItems)
        }
  
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    handleCheckout(event) {
        const button = event.target.closest("button[name='checkout']");
        if (!button) return;

        const checkoutScope = button.closest("cart-drawer, form, .cart-drawer__footer");
        const script =
            checkoutScope?.querySelector('.gtm-begin-checkout-script [type="application/ld+json"]') ||
            button.parentElement.querySelector('[type="application/ld+json"]');
        if (!script) return;

        let settings = null;
        try {
            const raw = (script.textContent || "").trim();
            if (!raw) return;
            settings = JSON.parse(raw);
        } catch (e) {
        }

        if (!window.dataLayer || !settings) return;

        let payload = settings;
        try {
            payload = JSON.parse(JSON.stringify(settings));
        } catch (e) {}

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(payload);

     
    }
    setTrackingConsentCookie() {
        const GTM_MIRROR_COOKIE = '_ctek_gtm_consent_flags';

        const clearLegacyThemeMirrorIfPresent = () => {
            try {
                const m = document.cookie.match(/(?:^|;\s*)_tracking_consent=([^;]*)/);
                if (!m) return;
                const raw = decodeURIComponent(m[1].trim());
                if (/^0[fF][aA][mM]\.0$/.test(raw)) {
                    document.cookie =
                        '_tracking_consent=; path=/; Max-Age=0; SameSite=Lax';
                }
            } catch (e) {}
        };

        const parseFamCookie = (raw) => {
            if (!raw || !/^0[fF][aA][mM]\.0$/.test(raw)) return null;
            return {
                preferencesAllowed: raw[1] === 'F',
                analyticsAllowed: raw[2] === 'A',
                marketingAllowed: raw[3] === 'M'
            };
        };

        const pushConsentToDataLayer = (functional, analytics, marketing, source) => {
            window.dataLayer = window.dataLayer || [];
            let flag = '0';
            flag += functional ? 'F' : 'f';
            flag += analytics ? 'A' : 'a';
            flag += marketing ? 'M' : 'm';
            const cookieValue = `${flag}.0`;
            window.dataLayer.push({
                event: 'ctek_shopify_consent_update',
                ctek_consent_string: cookieValue,
                preferences_consent: !!functional,
                analytics_consent: !!analytics,
                marketing_consent: !!marketing,
                ctek_consent_source: source || 'unknown'
            });
        };

        const updateCookieFromDetail = (detail, source) => {
            if (!detail) return;

            const functional = detail.preferencesAllowed;
            const analytics = detail.analyticsAllowed;
            const marketing = detail.marketingAllowed;

            let flag = '0';
            flag += functional ? 'F' : 'f';
            flag += analytics ? 'A' : 'a';
            flag += marketing ? 'M' : 'm';

            const cookieValue = `${flag}.0`;
            document.cookie = `${GTM_MIRROR_COOKIE}=${encodeURIComponent(cookieValue)}; path=/; SameSite=Lax`;
            pushConsentToDataLayer(functional, analytics, marketing, source);
        };

        clearLegacyThemeMirrorIfPresent();

        try {
            const m = document.cookie.match(
                new RegExp(
                    '(?:^|;\\s*)' +
                        GTM_MIRROR_COOKIE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                        '=([^;]*)'
                )
            );
            if (m) {
                const parsed = parseFamCookie(decodeURIComponent(m[1].trim()));
                if (parsed) {
                    pushConsentToDataLayer(
                        parsed.preferencesAllowed,
                        parsed.analyticsAllowed,
                        parsed.marketingAllowed,
                        'ctek_gtm_consent_flags_cookie'
                    );
                }
            }
        } catch (e) {}

        document.addEventListener('visitorConsentCollected', (event) => {
            clearLegacyThemeMirrorIfPresent();
            updateCookieFromDetail(event.detail, 'visitorConsentCollected');
        });
    }

    handleSelectItem(event) {
        // If user clicked an add-to-cart control inside a selectable link/card,
        // do not treat it as a select-item interaction.
        if (event.target.closest('[data-action="add-to-cart"]')) return;
        if (event.target.closest('form[data-type="add-to-cart-form"]')) return;

        let target = event.target.closest('[data-action="ga-send-select"]');
        if (!target || !window.dataLayer) return;

        const settings = this.getSelectSettings(target);
        if (!settings) return;

        const customEvent = {
            event: "custom_select_item",
            ecommerce: {
                selected_item_list_id: settings.list_id,
                selected_item_list_name: settings.list_name,
                currency: ShopifyAnalytics.meta.currency,
                items: [settings]
            }
        }
    
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    handleSearchIcon(event) {
        const target = event.target.closest('[data-action="open-drawer"][data-toggle-id="quickSearch"]');
        if (!target || !window.dataLayer) return;

        const now = Date.now();
        if (now - this.lastSearchIconTs < 500) return;
        this.lastSearchIconTs = now;

        const customEvent = {
            event: "search_icon",
            toggle_id: target.dataset.toggleId || "quickSearch"
        };

        window.dataLayer.push(customEvent);
    }

    handleProductImageClick(event) {
        const target = event.target.closest(".product-slider__image, .pswp__img");
        if (!target || !window.dataLayer) return;

        let gtmItem = null;
        try {
            const gtmScript = document.querySelector('.main-product__gtm-script script[type="application/ld+json"]');
            const gtmData = gtmScript ? JSON.parse(gtmScript.textContent || "") : null;
            gtmItem = gtmData?.ecommerce?.items?.[0] || null;
        } catch (e) {
            gtmItem = null;
        }

        const productTitle = gtmItem?.item_name || ShopifyAnalytics?.meta?.product?.title || "";
        const variantId = gtmItem?.item_id || ShopifyAnalytics?.meta?.product?.selectedVariantId || "";

        const customEvent = {
            event: "product_image_click",
            product_title: productTitle,
            variant_id: variantId
        };

        window.dataLayer.push(customEvent);
    }

    handleHomepageHeroBannerCta(event) {
        const target = event.target.closest("banner-hero .Button");
        if (!target || !window.dataLayer) return;

        const text = (target.querySelector(".button-txt")?.textContent || "").trim();
        const href = target.getAttribute("href") || target.dataset.href || "";

        const customEvent = {
            event: "homepage_hero_banner_cta",
            text,
            href
        };

        window.dataLayer.push(customEvent);
    }

    handleHomepagePopularProductsTabs(event) {
        const target = event.target.closest('.featured-collection .Button[data-action="toggle-collection"]');
        if (!target || !window.dataLayer) return;

        const text = (target.querySelector(".button-txt")?.textContent || target.textContent || "").trim();

        const customEvent = {
            event: "homepage_popular_products_tabs",
            text        
        };

        window.dataLayer.push(customEvent);
    }

    handleMenuInteraction(event) {
        const target = event.target.closest(
          '.header-hamburger-btn, [data-action="toggle-drawer"][data-toggle-id="mobile-menu-drawer"]'
        );
        if (!target || !window.dataLayer) return;

        // Both delegated and capture-phase fallback listeners can see the same click.
        // Deduplicate per-target to avoid double firing.
        const now = (typeof performance !== "undefined" && typeof performance.now === "function")
          ? performance.now()
          : Date.now();
        const lastFiredTs = this.menuInteractionLastFiredTs.get(target) || 0;
        if (now - lastFiredTs < 250) return;
        this.menuInteractionLastFiredTs.set(target, now);

        const customEvent = {
            event: "menu_interaction",
            aria_label: target.getAttribute("aria-label") || (target.querySelector("a,button")?.textContent || target.textContent || "").trim(),
            toggle_id: target.getAttribute("data-toggle-id") || target.dataset.toggleId || ""
        };

        window.dataLayer.push(customEvent);
    }

    handleProductUpsellTabs(event) {
        const target = event.target.closest(".product-recommendation--nav-button");
        if (!target || !window.dataLayer) return;

        const text = (target.textContent || "").trim();
        const tabId = target.getAttribute("data-tab") || target.dataset.tab || target.getAttribute("data-id") || target.dataset.id || "";

        const customEvent = {
            event: "product_upsell_tabs",
            text,
            tab_id: tabId
        };

        window.dataLayer.push(customEvent);
    }

    handleCollectionsTabs(event) {
        const target = event.target.closest("main-collection-grid .featured_collection__toggle-button");
        if (!target || !window.dataLayer) return;

        const titleElement = target.querySelector(".button-txt");
        const tabTitle = (titleElement?.textContent || target.textContent || "").trim();
        if (!tabTitle) return;

        const customEvent = {
            event: "collections_tabs",
            title: tabTitle
        };

        window.dataLayer.push(customEvent);
    }

    handleProductTabs(event) {
        const target = event.target.closest("drawer-product-info [data-slide-id], .pm__faq-button");
        if (!target || !window.dataLayer) return;

        const tabTitle = (target.dataset.slideId || target.dataset.slide || target.querySelector(".pm__faq-button-text")?.textContent || "").trim();
        if (!tabTitle) return;

        const customEvent = {
            event: "product_tabs",
            title: tabTitle
        };

        window.dataLayer.push(customEvent);
    }

    handleCartUpsellClick(event) {
        // Prevent firing click event when user is actually clicking an add-to-cart control.
        if (event.target.closest('[data-action="add-to-cart"]')) return;
        if (event.target.closest('form[data-type="add-to-cart-form"]')) return;

        const target = event.target.closest(".cart-drawer__rec-item .pc__wrapper-link");
        if (!target || !window.dataLayer) return;

        const settings = this.getSelectSettings(target);
        if (!settings) return;

        const customEvent = {
            event: "cart_upsell_click",
            ecommerce: {
                selected_item_list_id: settings.list_id || "",
                selected_item_list_name: settings.list_name || "",
                currency: ShopifyAnalytics.meta.currency,
                items: [settings]
            }
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    observeProductFinder() {
        const popup = document.querySelector("product-selector-popup");
        if (!popup || popup.dataset.gtmObserved === "true") return;
        popup.dataset.gtmObserved = "true";

        // Popup open/close is driven by aria-hidden, results view by the ps-wrapper--results class.
        const popupObserver = new MutationObserver(() => {
            const isOpen = popup.getAttribute("aria-hidden") === "false";
            if (isOpen && !this.productFinderIsOpen) {
                this.productFinderIsOpen = true;
                this.productFinderCloseReason = null;
                this.productFinderStepSelections = {};
                this.productFinderFormStart();
            } else if (!isOpen && this.productFinderIsOpen) {
                this.productFinderIsOpen = false;
                this.productFinderResultsFired = false;
                // Driven off the popup's own state so every close path is covered:
                // the X, Escape, an overlay click, or the CTA toggled a second time.
                this.productFinderClose();
            }

            if (!popup.classList.contains("ps-wrapper--results")) {
                this.productFinderResultsFired = false;
            }
        });

        popupObserver.observe(popup, { attributes: true, attributeFilter: ["aria-hidden", "class"] });

        // Adding a result to the cart auto-closes the popup — that is a conversion, not an exit.
        popup.addEventListener("product:added", () => this.setProductFinderCloseReason("add_to_cart"));

        // Products are injected asynchronously after the results view is revealed,
        // so "user sees the recommendation" is the moment cards land in the swiper.
        const resultsWrapper = popup.querySelector(".ps-popup__results .swiper-wrapper");
        if (resultsWrapper) {
            const resultsObserver = new MutationObserver(() => {
                if (this.productFinderResultsFired) return;
                if (!popup.classList.contains("ps-wrapper--results")) return;
                if (!resultsWrapper.querySelector("product-card")) return;

                this.productFinderResultsFired = true;
                this.productFinderFormFinished(popup, resultsWrapper);
            });

            resultsObserver.observe(resultsWrapper, { childList: true });
        }

        this.productFinderIsOpen = popup.getAttribute("aria-hidden") === "false";
    }

    // First reason wins: Escape sets "escape" on keydown, then global.js re-clicks the opener,
    // and that synthetic click must not overwrite it.
    setProductFinderCloseReason(reason) {
        if (!this.productFinderCloseReason) this.productFinderCloseReason = reason;
    }

    handleProductFinderClick(event) {
        const popup = document.querySelector("product-selector-popup");
        if (!popup) return;

        const isOpen = popup.getAttribute("aria-hidden") === "false";
        const toggle = event.target?.closest?.('[data-action="toggle-popup"][aria-controls="product-selector-popup"]');

        if (toggle) {
            // The same markup opens and closes: the CTA outside the popup, the X inside it.
            // Only a click that actually opens the popup is a CTA click.
            if (isOpen) {
                this.setProductFinderCloseReason(toggle.closest("product-selector-popup") ? "close_button" : "cta_toggle");
                return;
            }

            if (!window.dataLayer) return;

            const customEvent = {
                event: "product_finder_CTA",
                text: (toggle.querySelector(".button-txt")?.textContent || toggle.textContent || "").trim(),
                href: toggle.getAttribute("href") || ""
            };

            window.dataLayer.push(customEvent);
            console.log("dataLayer push -> product_finder_CTA:", customEvent);
            return;
        }

        // Overlay clicks close the popup without going through any toggle button.
        if (isOpen && !popup.contains(event.target)) {
            this.setProductFinderCloseReason("outside");
        }
    }

    handleProductFinderEscape(event) {
        if (event.key !== "Escape") return;

        const popup = document.querySelector("product-selector-popup");
        if (popup?.getAttribute("aria-hidden") !== "false") return;

        this.setProductFinderCloseReason("escape");
    }

    productFinderClose() {
        if (!window.dataLayer) return;

        const customEvent = {
            event: "product_finder_close",
            close_method: this.productFinderCloseReason || "other"
        };

        this.productFinderCloseReason = null;

        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> product_finder_close:", customEvent);
    }

    productFinderFormStart() {
        if (!window.dataLayer) return;

        const customEvent = { event: "product_finder_form_start" };

        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> product_finder_form_start:", customEvent);
    }

    // Fires when the user commits a step via its Next button, not on each option click:
    // steps allow multiple selections and can be revisited, so option clicks are not "finished".
    handleProductFinderStep(event) {
        const button = event.target.closest("product-selector-popup .ps-step__next-button");
        if (!button || !window.dataLayer) return;

        // The button is disabled until the step has a selection, but guard anyway.
        if (button.hasAttribute("disabled")) return;

        const slide = button.closest(".swiper-slide");
        const slides = button.closest(".ps-swiper .swiper-wrapper");
        if (!slide || !slides) return;

        const stepIndex = Array.from(slides.querySelectorAll(":scope > .swiper-slide")).indexOf(slide);
        if (stepIndex === -1) return;

        const selected = Array.from(slide.querySelectorAll(".ps-step__option-button[selected]"));
        if (!selected.length) return;

        const optionTags = selected.map((option) => option.getAttribute("data-tag") || "").filter(Boolean);

        // Users can jump back to a completed step and click Next again. Only re-report the step
        // if the answer actually changed, so plain back/forward navigation stays out of the funnel.
        const selectionKey = optionTags.join("|");
        if (this.productFinderStepSelections[stepIndex] === selectionKey) return;
        this.productFinderStepSelections[stepIndex] = selectionKey;

        const customEvent = {
            event: `product_finder_step${stepIndex + 1}_finished`,
            step_number: stepIndex + 1,
            step_name: (slide.querySelector(".ps-step__step-count-text span")?.textContent || "").trim(),
            option_texts: selected.map((option) => (option.textContent || "").trim()).filter(Boolean),
            option_tags: optionTags
        };

        window.dataLayer.push(customEvent);
        console.log(`dataLayer push -> ${customEvent.event}:`, customEvent);
    }

    productFinderFormFinished(popup, resultsWrapper) {
        if (!window.dataLayer) return;

        const selectedOptions = Array.from(popup.querySelectorAll(".ps-step__option-button[selected]"))
            .map((button) => button.getAttribute("data-tag") || (button.textContent || "").trim())
            .filter(Boolean);

        const productIds = Array.from(resultsWrapper.querySelectorAll("product-card"))
            .map((card) => (card.querySelector('input[name="id"]')?.value || "").trim())
            .filter(Boolean);

        const customEvent = {
            event: "product_finder_form_finished",
            selected_options: selectedOptions,
            product_ids: productIds,
            product_count: productIds.length,
            is_fallback: popup.classList.contains("using-fallback-products")
        };

        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> product_finder_form_finished:", customEvent);
    }

    handleProductFinderReadMore(event) {
        const card = event.target?.closest?.("product-selector-popup .ps-popup__results-wrapper product-card");
        if (!card || !window.dataLayer) return;

        // Add to cart is tracked separately — don't report it as a product click.
        if (event.target.closest('[data-action="add-to-cart"]')) return;

        const slide = card.closest(".swiper-slide");
        const slides = card.closest(".swiper-wrapper");
        const position = slide && slides
            ? Array.from(slides.querySelectorAll(":scope > .swiper-slide")).indexOf(slide) + 1
            : 0;

        const customEvent = {
            event: "product_finder_read_more",
            product_title: (card.querySelector(".pc__information__title")?.textContent || "").trim(),
            product_url: card.querySelector("a.pc__wrapper-link")?.getAttribute("href") || "",
            position
        };

        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> product_finder_read_more:", customEvent);
    }

    handleProductFinderRenew(event) {
        const target = event.target.closest('product-selector-popup [data-action="refresh-selector"]');
        if (!target || !window.dataLayer) return;

        const customEvent = { event: "product_finder_renew" };

        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> product_finder_renew:", customEvent);
    }

    getSelectSettings(target) {
        if (!target) return null;

        const rawSettings =
            target.dataset.selectSettings ||
            target.getAttribute("data-select-settings") ||
            "";
        if (!rawSettings) return null;

        try {
            return JSON.parse(rawSettings);
        } catch (e) {
            // Fallback for HTML-encoded attributes such as &quot;.
            try {
                const decodedSettings = rawSettings
                    .replaceAll("&quot;", '"')
                    .replaceAll("&#34;", '"');
                return JSON.parse(decodedSettings);
            } catch (error) {
                return null;
            }
        }
    }

    handleHomepagePopularProductsAddToCart(event) {
        const target = event.target.closest('[data-action="add-to-cart"]');
        if (!target || !window.dataLayer) return;

        const featuredCollection = target.closest(".featured-collection__homepage");
        if (!featuredCollection) return;

        this.homepagePopularProductsAtcPending = true;
    }

    handleHomepagePopularProductsFormSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const featuredCollection = form.closest(".featured-collection__homepage");
        if (!featuredCollection) return;

        this.homepagePopularProductsAtcPending = true;
    }

    handleCollectionsQuickAddToCart(event) {
        const target = event.target.closest('main-collection-grid [data-action="add-to-cart"]');
        if (!target || !window.dataLayer) return;

        this.collectionsQuickAddToCartPending = true;
    }

    handleCollectionsQuickAddToCartFormSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const collectionGrid = form.closest("main-collection-grid");
        if (!collectionGrid) return;

        this.collectionsQuickAddToCartPending = true;
    }

    handleProductStickyAddToCart(event) {
        const target = event.target.closest('.pm__sticky-atc [data-action="add-to-cart"]');
        if (!target || !window.dataLayer) return;

        this.productStickyAddToCartPending = true;
    }

    handleProductStickyAddToCartFormSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const stickyAtc = form.closest(".pm__sticky-atc");
        if (!stickyAtc) return;

        this.productStickyAddToCartPending = true;
    }

    handleProductUpsellAddToCart(event) {
        const target = event.target.closest('product-recommendations [data-action="add-to-cart"]');
        if (!target || !window.dataLayer) return;

        this.productUpsellAddToCartPending = true;
    }

    handleProductUpsellAddToCartFormSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const productRecommendations = form.closest("product-recommendations");
        if (!productRecommendations) return;

        this.productUpsellAddToCartPending = true;
    }

    handleCartUpsellAddToCart(event) {
        const target = event.target.closest('.cart-drawer__recommendations-wrapper [data-action="add-to-cart"]');
        if (!target || !window.dataLayer) return;

        this.cartUpsellAddToCartPending = true;
    }

    handleCartUpsellAddToCartFormSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const recommendationsWrapper = form.closest(".cart-drawer__recommendations-wrapper");
        if (!recommendationsWrapper) return;

        this.cartUpsellAddToCartPending = true;
    }

    handleProductAdded(event) {
        const sourceElement = event.target;
        if (!sourceElement) return;

        const featuredCollection = sourceElement.closest(".featured-collection__homepage");
        if (featuredCollection) {
            this.homepagePopularProductsAtcPending = false;
            this.homepagePopularProductsAddedToCart();
        }

        const collectionGrid = sourceElement.closest("main-collection-grid");
        if (collectionGrid) {
            this.collectionsQuickAddToCartPending = false;
            this.collectionsQuickAddToCart();
        }

        const stickyAtc = sourceElement.closest(".pm__sticky-atc");
        if (stickyAtc) {
            this.productStickyAddToCartPending = false;
            this.productStickyAddToCart();
        }

        const productRecommendations = sourceElement.closest("product-recommendations");
        if (productRecommendations) {
            this.productUpsellAddToCartPending = false;
            this.productUpsellAddToCart();
        }

        const recommendationsWrapper = sourceElement.closest(".cart-drawer__recommendations-wrapper");
        if (recommendationsWrapper) {
            this.cartUpsellAddToCartPending = false;
            this.cartUpsellAddToCart();
        }
    }

    homepagePopularProductsAddedToCart(cartItems = this.lastAddedCartItems) {
        if (!window.dataLayer) return;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            event: "homepage_popular_products_add_to_cart",
            ecommerce: this.buildEcommerce(cartItems)
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    collectionsQuickAddToCart(cartItems = this.lastAddedCartItems) {
        if (!window.dataLayer) return;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            event: "collections_quick_add_to_cart",
            ecommerce: this.buildEcommerce(cartItems)
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    productStickyAddToCart(cartItems = this.lastAddedCartItems) {
        if (!window.dataLayer) return;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            event: "product_sticky_add_to_cart",
            ecommerce: this.buildEcommerce(cartItems)
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    productUpsellAddToCart(cartItems = this.lastAddedCartItems) {
        if (!window.dataLayer) return;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            event: "product_upsell_add_to_cart",
            ecommerce: this.buildEcommerce(cartItems)
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    cartUpsellAddToCart(cartItems = this.lastAddedCartItems) {
        if (!window.dataLayer) return;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            event: "cart_upsell_add_to_cart",
            ecommerce: this.buildEcommerce(cartItems)
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    normalizeCategory(categoryValue) {
        const normalizedValue = String(categoryValue || "").trim();
        if (!normalizedValue) return "Uncategorized";

        const lowerCaseValue = normalizedValue.toLowerCase();
        if (lowerCaseValue === "uncategorized" || lowerCaseValue === "okategoriserat") {
            return "Uncategorized";
        }

        return normalizedValue;
    }

    getGtmDataFromDom(item) {
        const itemKey = item?.key;
        if (!itemKey) return null;

        const cartItemElement = document.querySelector(`[data-gtm-id="${CSS.escape(itemKey)}"]`);
        if (!cartItemElement) return null;

        return {
            category: cartItemElement.dataset.gtmCategory || "",
            category2: cartItemElement.dataset.gtmCategory2 || "",
            itemBrand: cartItemElement.dataset.gtmItemBrand || ""
        };
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        

        cartItems.forEach((item) => {
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;

            const domData = this.getGtmDataFromDom(item);
            const category = this.normalizeCategory(
                item.properties?.["_gtm-category"] ||
                domData?.category ||
                item.product_type
            );
            const category2 = this.normalizeCategory(
                item.properties?.["_gtm-category2"] ||
                domData?.category2 ||
                item.product_type
            );
            const itemBrand =
                item.properties?.["_gtm-item_brand"] ||
                domData?.itemBrand ||
                item.vendor ||
                "";
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': category,
                'category2': category2,
                'item_brand': itemBrand,
                'list_id': item.properties?.["_gtm-list_id"] || "",
                'list_name': item.properties?.["_gtm-list_name"] || ""
            }

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'items': items
        }
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        let quantity = null;

        if (typeof body.get === "function") {
            // FormData / URLSearchParams
            quantity = body.get("quantity");
        } else if (typeof body === "string") {
            // JSON string payload
            try {
                const parsedBody = JSON.parse(body);
                quantity = parsedBody?.quantity ?? null;
            } catch (e) {
                quantity = null;
            }
        } else if (typeof body === "object") {
            // Plain object payload
            quantity = body.quantity ?? null;
        }

        if (quantity) cartItems[0].quantity = Number(quantity);

        return cartItems;
    }
}

new gTagEvents();
